"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { getPersonalizedNewsData } from "../../../services/TradingMallService";
import type { IWatchlistNewsData, IPersonalizedNewsRequest } from "../../../interfaces/News.interface";
import type { INewsFilters } from "../../../interfaces/NewsFilter.interface";
import { buildFilterArray } from "../utils/filterUtils";

interface UsePersonalizedNewsProps {
  newsType: IPersonalizedNewsRequest["newsType"];
  filters: INewsFilters;
  limit?: number;
  authCode?: string | null;
}

interface UsePersonalizedNewsReturn {
  newsData: IWatchlistNewsData;
  isLoading: boolean;
  page: number;
  handleShowMore: () => void;
  handleShowLess: () => void;
  refetch: () => void;
}

const initialNewsData: IWatchlistNewsData = {
  news: [],
  list: [],
  statusCode: null,
  showMore: false,
  showLess: false,
};

export const usePersonalizedNews = ({
  newsType,
  filters,
  limit = 5,
  authCode,
}: UsePersonalizedNewsProps): UsePersonalizedNewsReturn => {
  const [newsData, setNewsData] = useState<IWatchlistNewsData>(initialNewsData);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);

  const filter = useMemo(() => buildFilterArray(filters), [filters]);

  const fetchNews = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getPersonalizedNewsData({
        newsType,
        filters: filter,
        page,
        limit,
        ...(authCode ? { authCode } : {}),
      });

      setNewsData((prevData) => ({
        news: page > 1
          ? [...prevData.news, ...(res?.data?.news ?? [])]
          : (res?.data?.news ?? []),
        list: res?.data?.list ?? [],
        statusCode: res?.statusCode ?? null,
        showMore: res?.data?.showMore ?? false,
        showLess: res?.data?.showLess ?? false,
      }));
    } catch (error) {
      console.error(`Failed to fetch ${newsType} news:`, error);
      setNewsData({ ...initialNewsData, statusCode: 99 });
    } finally {
      setIsLoading(false);
    }
  }, [newsType, filter, page, limit]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Fetch news when filter or page changes
  useEffect(() => {
    fetchNews();
  }, [page,filter]);

  const handleShowMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const handleShowLess = useCallback(() => {
    setPage(1);
  }, []);

  const refetch = useCallback(() => {
    setPage(1);
    fetchNews();
  }, [fetchNews]);

  return {
    newsData,
    isLoading,
    page,
    handleShowMore,
    handleShowLess,
    refetch,
  };
};

export default usePersonalizedNews;
